import React, { memo, useCallback, useMemo } from 'react';
import { Alert, Button, Col, DatePicker, Divider, Form, Input, Row, Typography } from 'antd';
import { EuroOutlined } from '@ant-design/icons';
import Modal from '../../../../components/reusable/Modal';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteDebtor,
  getCompanyOrPersonCoveredDebtsList,
  getCompanyOrPersonUncoveredDebtsList,
  notifyIncorrectDebt,
  updateDebtAmount,
  updateDebtPayment,
} from '../../../../store/entities/administration/thunks';
import { checkDateValidity } from '../../../../helpers/common';
import dayjs from 'dayjs';
import { ReactComponent as Icon } from 'assets/img/icons/cardIcons/contactInfo.svg';
import { BlockHeader } from '../../../../components/reusable/BlockHeader';
import { InputDataRow } from '../../../../components/reusable/CompanyDataCard';
import TextArea from 'antd/es/input/TextArea';
import { setSuccessModalVisible } from '../../../../store/entities/administration';
const { Text, Title } = Typography;

/**
 * Shows one of three available modal versions when user clicks on
 */
export const IncreaseDecreaseDebtModal = memo(({ companyOrPerson }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const {
    selectedDebtor,
    debtManagementModals,
    updateDebtPaymentState,
    updateDebtAmountState,
    notifyIncorrectDebtState,
    deleteDebtorState,
  } = useSelector(state => state.administration);

  const currentModalKey = Object.keys(debtManagementModals).find(key => debtManagementModals[key]);

  const refreshTables = useCallback(async () => {
    await dispatch(
      getCompanyOrPersonCoveredDebtsList({
        companyOrPerson: companyOrPerson,
      })
    );
    await dispatch(
      getCompanyOrPersonUncoveredDebtsList({
        companyOrPerson: companyOrPerson,
      })
    );
    form.resetFields();
  }, [companyOrPerson, dispatch, form, selectedDebtor.id]);

  const ReusableContent = ({ children, disclaimer }) => {
    return (
      <Row gutter={[0, 4]}>
        <Col span={24}>{disclaimer}</Col>
        <Col span={24} className="color-darkergray">
          <InputDataRow
            label={t(
              `administration.debtManagement.isCoveredPartiallyCoveredOpen.${
                companyOrPerson === 'company' ? 'company' : 'individual'
              }.companyName`
            )}
            input={<Text className="color-darkergray">{selectedDebtor?.companyName || '-'}</Text>}
          />
        </Col>
        <Divider style={{ margin: '0 4px' }} />
        <Col span={24} className="color-darkergray">
          <InputDataRow
            label={t(
              `administration.debtManagement.isCoveredPartiallyCoveredOpen.${
                companyOrPerson === 'company' ? 'company' : 'individual'
              }.debtId`
            )}
            input={<Text className="color-darkergray">{selectedDebtor?.debtId || '-'}</Text>}
          />
        </Col>
        <Col span={24} className="color-darkergray">
          <InputDataRow
            label={t(
              `administration.debtManagement.isCoveredPartiallyCoveredOpen.${
                companyOrPerson === 'company' ? 'company' : 'individual'
              }.submissionDate`
            )}
            input={<Text className="color-darkergray">{selectedDebtor?.submissionDate || '-'}</Text>}
          />
        </Col>
        <Col span={24} className="color-darkergray">
          <InputDataRow
            label={t(
              `administration.debtManagement.isCoveredPartiallyCoveredOpen.${
                companyOrPerson === 'company' ? 'company' : 'individual'
              }.dateIncurrence`
            )}
            input={<Text className="color-darkergray">{selectedDebtor?.dateIncurrence || '-'}</Text>}
          />
        </Col>
        <Col span={24} className="color-darkergray">
          <InputDataRow
            label={t(
              `administration.debtManagement.isCoveredPartiallyCoveredOpen.${
                companyOrPerson === 'company' ? 'company' : 'individual'
              }.debtType`
            )}
            input={<Text className="color-darkergray">{selectedDebtor?.debtType || '-'}</Text>}
          />
        </Col>
        <Col span={24} className="color-darkergray">
          <InputDataRow
            label={t(
              `administration.debtManagement.isCoveredPartiallyCoveredOpen.${
                companyOrPerson === 'company' ? 'company' : 'individual'
              }.amount`
            )}
            input={<Text className="color-darkergray">{selectedDebtor?.amount || '-'}</Text>}
          />
        </Col>
        <Divider style={{ margin: '0 4px' }} />
        {children}
      </Row>
    );
  };

  const modalContent = useMemo(() => {
    switch (currentModalKey) {
      case 'isCoveredPartiallyCoveredOpen':
        return {
          content: (
            <ReusableContent>
              <>
                <Col span={24}>
                  <InputDataRow
                    label={t('professionalReport.negativeData.paymentHistory.detailedInformation.paymentDate')}
                    input={
                      <Form.Item name="paymentDate" initialValue={dayjs()}>
                        <DatePicker
                          size="small"
                          className="datepicker"
                          style={{ width: '100%' }}
                          placeholder={t(
                            `professionalReport.negativeData.paymentHistory.detailedInformation.paymentDate`
                          )}
                        />
                      </Form.Item>
                    }
                  />
                </Col>
                <Col span={24}>
                  <InputDataRow
                    label={t('administration.debtManagement.uncoveredDebtAmount')}
                    input={
                      <Form.Item name="amount" normalize={value => value.replace(',', '.')}>
                        <Input
                          size="small"
                          addonAfter={<EuroOutlined />}
                          placeholder={t('administration.debtManagement.ammount.placeholder')}
                        />
                      </Form.Item>
                    }
                  />
                </Col>
              </>
            </ReusableContent>
          ),
          modalTitle: (
            <BlockHeader title={t('common.coveredOrPartially')} icon={<Icon style={{ height: 30, width: 30 }} />} />
          ),
          onCloseActionType: 'TOGGLE_COVERED_PARTIALLY_COVERED_MODAL',
          onSubmit: async formVal => {
            const isDateValid = checkDateValidity(formVal.paymentDate);

            if (!isDateValid) return;

            const updateDebtReq = await dispatch(
              updateDebtPayment({
                ...formVal,
                caseNr: selectedDebtor?.debtsId,
                id: selectedDebtor?.id,
                companyOrPerson,
              })
            );

            if (!updateDebtReq.error) {
              await refreshTables();
              dispatch(setSuccessModalVisible(true));
            }
          },
          error: updateDebtPaymentState.errorMsg && (
            <Alert
              message={updateDebtPaymentState.errorMsg}
              companyOrPerson="error"
              showIcon
              style={{ marginTop: '2rem', marginBottom: '1rem' }}
            />
          ),
          isLoading: updateDebtPaymentState.loading,
        };
      case 'isIncreaseDebtOpen':
        return {
          content: (
            <ReusableContent>
              <Col span={24}>
                <InputDataRow
                  label={t('common.debtAmountAfterIncrease')}
                  input={
                    <Form.Item name="amount" normalize={value => value.replace(',', '.')}>
                      <Input
                        size="small"
                        addonAfter={<EuroOutlined />}
                        placeholder={t('administration.debtManagement.ammount.placeholder')}
                      />
                    </Form.Item>
                  }
                />
              </Col>
            </ReusableContent>
          ),
          modalTitle: (
            <BlockHeader title={t('common.increaseDebt')} icon={<Icon style={{ height: 30, width: 30 }} />} />
          ),
          onCloseActionType: 'TOGGLE_INCREASE_DEBT_MODAL',
          onSubmit: async formVal => {
            const updateDebtReq = await dispatch(
              updateDebtAmount({ ...formVal, caseNr: selectedDebtor?.debtsId, id: selectedDebtor?.id, companyOrPerson })
            );

            if (!updateDebtReq.error) {
              await refreshTables();
              dispatch(setSuccessModalVisible(true));
            }
          },
          error: updateDebtAmountState.errorMsg && (
            <Alert
              message={updateDebtAmountState.errorMsg}
              companyOrPerson="error"
              showIcon
              style={{ marginTop: '2rem', marginBottom: '1rem' }}
            />
          ),
          isLoading: updateDebtAmountState.loading,
        };
      case 'isInformAboutIncorrectDebtOpen':
        return {
          content: (
            <ReusableContent disclaimer={<Title level={5}>{t('common.areYouSureInformIncorrectDebt')}</Title>}>
              <>
                <Row style={{ width: '100%' }}>
                  <Col span={24}>
                    <Form.Item name="comment">
                      <TextArea style={{ width: '100%' }} placeholder={t('common.leaveComment')} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row justify="end" style={{ width: '100%' }} gutter={16}>
                  <Col>
                    <Button
                      onClick={() => dispatch({ type: 'TOGGLE_INFORM_ABOUT_INCORRECT_DEBT_MODAL', payload: false })}
                      size="small"
                    >
                      {t('global.no')}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      htmlType="button"
                      size="small"
                      className="save"
                      onClick={async () => {
                        try {
                          await dispatch(
                            notifyIncorrectDebt({
                              id: selectedDebtor?.id,
                              companyOrPerson,
                              comment: form.getFieldValue('comment') || '',
                            })
                          );
                          dispatch({ type: 'TOGGLE_INFORM_ABOUT_INCORRECT_DEBT_MODAL', payload: false });
                          await refreshTables(); // Refresh data after success
                          dispatch(setSuccessModalVisible(true));
                        } catch (error) {
                          console.error(t('administration.debtManagement.notifyInccorrectDebt.error'), error);
                        }
                      }}
                    >
                      {t('global.yes')}
                    </Button>
                  </Col>
                </Row>
              </>
            </ReusableContent>
          ),
          modalTitle: (
            <BlockHeader
              title={t('common.informAboutIncorrectDebt')}
              icon={<Icon style={{ height: 30, width: 30 }} />}
            />
          ),
          onCloseActionType: 'TOGGLE_INFORM_ABOUT_INCORRECT_DEBT_MODAL',
          error: notifyIncorrectDebtState.errorMsg && (
            <Alert
              message={notifyIncorrectDebtState.errorMsg}
              companyOrPerson="error"
              showIcon
              style={{ marginTop: '2rem', marginBottom: '1rem' }}
            />
          ),
          isLoading: notifyIncorrectDebtState.loading,
        };
      case 'isDeleteDebtOpen':
        return {
          content: (
            <ReusableContent
              disclaimer={<Title level={5}>{t('administration.debtManagement.deleteDebt.title2')}</Title>}
            >
              <>
                <Row justify="end" style={{ width: '100%' }} gutter={16}>
                  <Col>
                    <Button onClick={() => dispatch({ type: 'TOGGLE_DELETE_DEBT_MODAL', payload: false })} size="small">
                      {t('global.no')}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      htmlType="button"
                      size="small"
                      className="save"
                      onClick={async () => {
                        try {
                          await dispatch(
                            deleteDebtor({
                              debtId: selectedDebtor?.id,
                              companyOrPerson,
                            })
                          );
                          dispatch({ type: 'TOGGLE_DELETE_DEBT_MODAL', payload: false });
                          await refreshTables(); // Refresh data after success
                          dispatch(setSuccessModalVisible(true));
                        } catch (error) {
                          console.error(t('administration.debtManagement.deleteDebt.error'), error);
                        }
                      }}
                    >
                      {t('global.yes')}
                    </Button>
                  </Col>
                </Row>
              </>
            </ReusableContent>
          ),
          modalTitle: (
            <BlockHeader
              title={t('administration.debtManagement.deleteDebt.title')}
              icon={<Icon style={{ height: 30, width: 30 }} />}
            />
          ),
          onCloseActionType: 'TOGGLE_DELETE_DEBT_MODAL',
          error: deleteDebtorState.errorMsg && (
            <Alert
              message={deleteDebtorState.errorMsg}
              companyOrPerson="error"
              showIcon
              style={{ marginTop: '2rem', marginBottom: '1rem' }}
            />
          ),
          isLoading: deleteDebtorState.loading,
        };
    }
  }, [
    deleteDebtorState,
    debtManagementModals,
    updateDebtPaymentState,
    updateDebtAmountState,
    t,
    companyOrPerson,
    notifyIncorrectDebtState,
  ]);

  return (
    <Modal
      isOpen={debtManagementModals[currentModalKey]}
      onCancel={() => dispatch({ type: modalContent.onCloseActionType, payload: false })}
      isLoading={modalContent?.isLoading}
      secondaryTitle={modalContent?.modalTitle}
      size="md"
      destroyOnClose
      className="new-debt"
    >
      <Form
        form={form}
        onFinish={modalContent?.onSubmit}
        layout="vertical"
        autoComplete="off"
        requiredMark={false}
        name="debtManagement"
      >
        {modalContent?.content}
        {/*{modalContent?.error}*/}
        {modalContent?.onSubmit && (
          <Form.Item wrapperCol={{ span: 24 }} style={{ alignItems: 'end', margin: 0, paddingTop: '0.5em' }}>
            <Button size="small" htmlTypge="submit" className="save">
              {t('common.updatePayment')}
            </Button>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
});
